
import { Record } from "../fable_modules/fable-library-js.5.3.0/Types.js";
import { list_type, int32_type, record_type, string_type } from "../fable_modules/fable-library-js.5.3.0/Reflection.js";
import { singleton, ofArray } from "../fable_modules/fable-library-js.5.3.0/List.js";
import { printf, toText, concat } from "../fable_modules/fable-library-js.5.3.0/String.js";
import { FeeAmountCentsModule_value } from "./BaseTypes.js";
import { fromParts, op_Multiply, toInt32 } from "../fable_modules/fable-library-js.5.3.0/Decimal.js";
import { ChallengeFeeType, ChallengeFee_amount } from "./Challenge.js";
import { int32ToString } from "../fable_modules/fable-library-js.5.3.0/Util.js";
import { PromotionModule_endDateLabel, PromotionModule_percentOff } from "./Promotion.js";

/**
 * A cited source, listed in the page's numbered Sources section.
 */
export class CopySource extends Record {
    constructor(Title, Publisher, Url, Accessed) {
        super();
        this.Title = Title;
        this.Publisher = Publisher;
        this.Url = Url;
        this.Accessed = Accessed;
    }
}

export function CopySource_$reflection() {
    return record_type("Shared.CopySource", [], CopySource, () => [["Title", string_type], ["Publisher", string_type], ["Url", string_type], ["Accessed", string_type]]);
}

/**
 * A paragraph of copy. `SourceIds` are 1-based positions in `FloodReliefCopy.sources`.
 */
export class CopyParagraph extends Record {
    constructor(Text$, SourceIds) {
        super();
        this.Text = Text$;
        this.SourceIds = SourceIds;
    }
}

export function CopyParagraph_$reflection() {
    return record_type("Shared.CopyParagraph", [], CopyParagraph, () => [["Text", string_type], ["SourceIds", list_type(int32_type)]]);
}

export class CopySection extends Record {
    constructor(Heading, Paragraphs) {
        super();
        this.Heading = Heading;
        this.Paragraphs = Paragraphs;
    }
}

export function CopySection_$reflection() {
    return record_type("Shared.CopySection", [], CopySection, () => [["Heading", string_type], ["Paragraphs", list_type(CopyParagraph_$reflection())]]);
}

/**
 * The body copy for one promoted charity's card, matched to the promotion by EIN.
 */
export class CharityCard extends Record {
    constructor(Ein, Body) {
        super();
        this.Ein = Ein;
        this.Body = Body;
    }
}

export function CharityCard_$reflection() {
    return record_type("Shared.CharityCard", [], CharityCard, () => [["Ein", string_type], ["Body", CopyParagraph_$reflection()]]);
}

export const FloodReliefCopy_heroHeading = "The floods are gone from the news. The need isn\'t.";

export const FloodReliefCopy_heroSub = new CopyParagraph("Recovery takes years, but most disaster giving is over within two months. Start a challenge that keeps help coming.", ofArray([1, 2]));

export const FloodReliefCopy_heroCta = "Start a challenge";

export const FloodReliefCopy_whyNow = new CopySection("Donations drop off long before the need does", singleton(new CopyParagraph("After a disaster, about a third of all giving happens in the first one to four weeks and two-thirds within two months. Six months on, giving has stopped. Recovery lasts far longer: the long-term phase can run for years, even decades.", ofArray([1, 2]))));

export const FloodReliefCopy_texas = new CopySection("Texas Hill Country, 2026", ofArray([new CopyParagraph("In July 2026 the Texas Hill Country flooded again, just over a year after the July 2025 floods. In Kerrville the water was worse than in 2025, and in some places it reached higher, but far fewer lives were lost.", ofArray([3, 4])), new CopyParagraph("In Kerr County, 135 homes were destroyed or heavily damaged, and about 490 more homes and 100 businesses were damaged. Some families who had rebuilt after 2025 were flooded again.", ofArray([3, 4, 5]))]));

export const FloodReliefCopy_nepal = new CopySection("Nepal, August 2026", singleton(new CopyParagraph("On August 26, 2026, glacial rock and ice collapsed off Langtang Lirung peak. The surge ran 72 kilometres down the Bhote Koshi and Trishuli rivers, flattening riverside villages, markets and bridges across eight districts.", singleton(6))));

export const FloodReliefCopy_charitiesHeading = "Where your challenge helps";

export const FloodReliefCopy_cards = ofArray([new CharityCard("742225369", new CopyParagraph("The Community Foundation of the Texas Hill Country administers the Kerr County Flood Relief Fund, set up for relief, recovery and resiliency after the July 4, 2025 flood. As of June 2026 it reported $82 million granted and 228 households on pathways to permanent housing.", singleton(7))), new CharityCard("951831116", new CopyParagraph("Direct Relief supports a locally led medical response in Nepal. As of September 23, 2026 it had sent 18.9 tons of medical aid to hospitals and mobile medical teams, and $125,000 in emergency grants to three Nepali organisations.", singleton(6)))]);

export const FloodReliefCopy_cardCta = "Start a challenge";

export function FloodReliefCopy_benefiting(charityName) {
    return concat("Benefiting ", charityName);
}

export const FloodReliefCopy_disclaimer = "Donations go to the named charity as an organisation and are used at its discretion.";

export const FloodReliefCopy_sourcesHeading = "Sources";

export const FloodReliefCopy_sources = ofArray([new CopySource("Disaster Relief: Help Now, Help Later, Help Better", "Center for High Impact Philanthropy, University of Pennsylvania", "https://www.impact.upenn.edu/how-to-help-in-a-disaster/", "2026-09-29"), new CopySource("Disaster Recovery", "Center for Disaster Philanthropy", "https://disasterphilanthropy.org/cdp-resource/disaster-recovery/", "2026-09-29"), new CopySource("Texas flood destroyed or heavily damaged 135 Kerr County homes, Abbott said", "The Texas Tribune, July 20, 2026", "https://www.texastribune.org/2026/07/20/texas-flood-kerr-county-homes-destroyed-greg-abbott/", "2026-09-29"), new CopySource("Photos: Almost 500 homes and 100 businesses damaged in Kerr County floods", "KUT Radio, July 20, 2026", "https://www.kut.org/energy-environment/2026-07-20/kerrville-flood-photos-texas-hill-country-kerr-county", "2026-09-29"), new CopySource("They Rebuilt After Texas\' Devastating Floods. Then Their Home Flooded Again.", "ProPublica and The Texas Tribune, August 24, 2026", "https://www.propublica.org/article/texas-flooding-2026-kerr-county", "2026-09-29"), new CopySource("Nepal Flood Relief 2026", "Direct Relief", "https://www.directrelief.org/emergency/nepal-floods-2026/", "2026-09-29"), new CopySource("Rebuild Kerr: Kerr County Flood Relief Fund", "Community Foundation of the Texas Hill Country", "https://rebuildkerr.org/", "2026-09-29")]);

export const FloodReliefCopy_shareDescription = "Start a personal challenge benefiting the Community Foundation of the Texas Hill Country or Direct Relief, two charities responding to floods in Texas and Nepal.";

/**
 * The promotional fee as shown in copy: whole dollars without cents ("$5"), otherwise with cents ("$7.50").
 */
export function FloodReliefCopy_feeLabel(promo) {
    const cents = FeeAmountCentsModule_value(promo.FeeCents) | 0;
    if ((cents % 100) === 0) {
        const arg = ~~(cents / 100) | 0;
        return toText(printf("$%d"))(arg);
    }
    else {
        const arg_1 = ~~(cents / 100) | 0;
        const arg_2 = (cents % 100) | 0;
        return toText(printf("$%d.%02d"))(arg_1)(arg_2);
    }
}

const FloodReliefCopy_standardFeeCents = ~~toInt32(op_Multiply(ChallengeFee_amount(new ChallengeFeeType(0, [])), fromParts(100, 0, 0, false, 0)));

/**
 * The promotion's discount from the standard fee, e.g. "73%".
 */
export function FloodReliefCopy_percentLabel(promo) {
    return int32ToString(PromotionModule_percentOff(FloodReliefCopy_standardFeeCents, promo)) + "%";
}

/**
 * The promotion's discount as copy, e.g. "73% off challenge fees".
 */
export function FloodReliefCopy_percentOffLabel(promo) {
    return concat(FloodReliefCopy_percentLabel(promo), " off challenge fees");
}

export function FloodReliefCopy_promoStrip(promo) {
    return `${FloodReliefCopy_percentOffLabel(promo)} through ${PromotionModule_endDateLabel(promo)}: a flat ${FloodReliefCopy_feeLabel(promo)} fee on challenges benefiting either charity below`;
}

export function FloodReliefCopy_cardFeeLine(promo) {
    return concat(FloodReliefCopy_feeLabel(promo), " flat fee through ", PromotionModule_endDateLabel(promo));
}

export function FloodReliefCopy_feeHeading(promo) {
    return concat("How the ", FloodReliefCopy_feeLabel(promo), " fee works");
}

/**
 * The fee explanation. It states no standard-fee or Charity Bonus figure: the create form shows exact amounts.
 */
export function FloodReliefCopy_feeSection(promo) {
    return `You pay a flat ${FloodReliefCopy_feeLabel(promo)} challenge fee, ${FloodReliefCopy_percentLabel(promo)} off the standard fee, when you create a challenge for either charity by ${PromotionModule_endDateLabel(promo)} (Central Time). Part of that fee can go to your charity through the Charity Bonus. Supporters' pledges go straight to the charity through Every.org.`;
}

/**
 * The Home promo band's eyebrow line.
 */
export function FloodReliefCopy_homePromoEyebrow(promo) {
    return concat("Flood relief · through ", PromotionModule_endDateLabel(promo));
}

/**
 * The Home promo band's body line. The percentage is the band's heading (`percentOffLabel`).
 */
export function FloodReliefCopy_homePromoBody(promo) {
    return concat("A flat ", FloodReliefCopy_feeLabel(promo), " challenge fee instead of the standard fee.");
}

/**
 * A Home promo start link's label, naming the cause's region, never a program.
 */
export function FloodReliefCopy_startChallengeLabel(charity) {
    return concat("Start a ", charity.Region, " challenge");
}

export const FloodReliefCopy_homePromoMoreLink = "Find out more";

export const FloodReliefCopy_homePromoMoreLinkAriaLabel = "Find out more about flood relief";

//# sourceMappingURL=FloodReliefCopy.js.map
